import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { TestCard } from '@/components/TestCard';
import { getPopularTests } from '@/data/tests';
import {
  MessageCircle,
  Shield,
  Clock,
  Home as HomeIcon,
  CheckCircle2,
  TrendingUp,
  Users,
  Award,
  ArrowRight
} from 'lucide-react';

export default function Home() {
  const popularTests = getPopularTests();

  useEffect(() => {
    // Scroll to section if hash is present
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, []);

  const features = [
    {
      icon: Shield,
      title: 'Accurate Results',
      description: 'State-of-the-art equipment ensuring 99.9% accuracy'
    },
    {
      icon: Clock,
      title: 'Fast Reports',
      description: 'Get your reports within 24 hours or less'
    },
    {
      icon: HomeIcon,
      title: 'Home Collection',
      description: 'Free sample collection from your doorstep'
    },
    {
      icon: Award,
      title: 'Certified Lab',
      description: 'NABL & ISO certified diagnostic center'
    }
  ];

  const stats = [
    { value: '50,000+', label: 'Tests Completed' },
    { value: '10,000+', label: 'Happy Patients' },
    { value: '24 hrs', label: 'Quick Reports' },
    { value: '99.9%', label: 'Accuracy Rate' }
  ];

  const benefits = [
    'NABL & ISO Certified Laboratory',
    'Experienced & Qualified Technicians',
    'Advanced Diagnostic Equipment',
    'Affordable Pricing',
    'Home Sample Collection',
    'Online Report Access'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-20 md:pt-24 pb-16 md:pb-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 gradient-hero opacity-50"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <Badge
              variant="secondary"
              className="mb-6 px-4 py-1.5 text-sm font-medium"
            >
              Trusted by 10,000+ Patients
            </Badge>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              D-Lab – Trusted{' '}
              <span className="text-primary">Diagnostic Laboratory</span>
            </h1>

            {/* Subheading */}
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
              Accurate tests. Fast reports. Easy booking. Your health, our
              priority.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <WhatsAppButton size="lg" className="w-full sm:w-auto" />
              <Button
                asChild
                variant="outline"
                size="lg"
                className="w-full sm:w-auto"
              >
                <Link to="/tests">
                  Browse All Tests
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-accent" />
                <span>NABL Certified</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-accent" />
                <span>ISO Accredited</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-accent" />
                <span>99.9% Accuracy</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-xl bg-card shadow-card"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Why Choose D-Lab?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We combine cutting-edge technology with compassionate care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card
                  key={index}
                  className="hover:shadow-card-hover transition-smooth group"
                >
                  <CardContent className="pt-6 text-center">
                    <div className="inline-flex p-4 bg-primary/10 rounded-2xl mb-4 group-hover:bg-primary/15 transition-smooth">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Popular Tests Section */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Popular Tests
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Most frequently booked diagnostic tests
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {popularTests.map((test) => (
              <TestCard key={test.id} test={test} />
            ))}
          </div>

          <div className="text-center">
            <Button asChild size="lg" variant="outline">
              <Link to="/tests">
                View All Tests
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                About D-Lab
              </h2>
              <p className="text-lg text-muted-foreground">
                Your trusted partner in diagnostic healthcare
              </p>
            </div>

            <Card className="shadow-card-hover">
              <CardContent className="pt-6">
                <div className="prose max-w-none">
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    D-Lab is a state-of-the-art diagnostic laboratory committed
                    to providing accurate, reliable, and timely diagnostic
                    services. With over 10 years of experience in the healthcare
                    industry, we have established ourselves as a trusted name in
                    diagnostic testing.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground">
                          {benefit}
                        </span>
                      </div>
                    ))}
                  </div>

                  <p className="text-muted-foreground leading-relaxed">
                    Our team of experienced pathologists and technicians work
                    round the clock to ensure that you receive the most accurate
                    results in the shortest possible time. We invest in the
                    latest diagnostic technology and follow strict quality
                    control measures to maintain the highest standards of
                    accuracy.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-16 md:py-20 bg-gradient-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Book Your Test?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Get in touch with us on WhatsApp for quick booking and instant
              support
            </p>
            <Button
              size="lg"
              variant="secondary"
              className="shadow-xl"
              onClick={() => {
                const message =
                  'Hello%20D-Lab,%0A%0AI%20would%20like%20to%20book%20a%20test.%0A%0APlease%20share%20the%20available%20slots.%0A%0AThank%20you.';
                window.open(
                  `https://wa.me/919370209058?text=${message}`,
                  '_blank'
                );
              }}
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Book Test on WhatsApp
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
